import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "oidc-react";
import { Box } from "@mui/material";
import { AgronodLink, AgronodTypography } from "@agronod/mui-components";
import { isPublicRoute } from "@/constants";
import { realmFromIssuer } from "@/config/realm";
import { AUTH_CALLBACK_PREFIXES } from "@/config/token-expiry-handlers";
import {
  buildRegistrationUrl,
  hasRegisteredClaim,
  isRegistered,
} from "@/config/registration-url";
import { useUser } from "@/hooks/useUser";
import { isLantbrukareUser } from "@/types/auth";
import { recordRegistrationRedirect } from "@/utils/registrationRedirectBreaker";
import { navigateExternal } from "@/utils/externalNavigation";
import FullScreenSpinner from "./FullScreenSpinner";

interface RegistrationGuardProps {
  children: React.ReactNode;
}

const isAuthCallbackRoute = (pathname: string): boolean =>
  AUTH_CALLBACK_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/** Shown when the two apps keep sending the user back and forth. */
const RedirectLoopNotice: React.FC<{ kontoUrl: string }> = ({ kontoUrl }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      gap: 2,
      px: 2,
      textAlign: "center",
    }}
  >
    <AgronodTypography variant="h6" component="h1">
      Vi kunde inte bekräfta din registrering
    </AgronodTypography>
    <AgronodTypography sx={{ color: "text.secondary" }}>
      Agrokoll och Agronod konto är inte överens om att ditt konto är registrerat.
      Försök igen om en stund, eller öppna Agronod konto.
    </AgronodTypography>
    <AgronodLink type="classic" href={kontoUrl}>
      Gå till Agronod konto
    </AgronodLink>
  </Box>
);

/**
 * Sends a lantbrukare who has not registered to Agronod konto, and brings them
 * back to this exact URL afterwards.
 *
 * The `registered` flag comes from `/auth/me` (`useUser`), which the backend
 * reads from the access token. It cannot be read client-side: the Keycloak
 * custom mapper writes its claims to the access token only, so the OIDC
 * profile (id token + userinfo) never carries it. `useUser` is the same cached
 * query the konto context and ProtectedRoute use, so this adds no request.
 *
 * Decision:
 * - pass through on B2B routes, auth callbacks, while the OIDC session is
 *   loading, for a non-lantbrukare issuer, when `/auth/me` failed (the page's
 *   own error handling takes over) and when the user is registered;
 * - wait on a spinner while `/auth/me` is in flight;
 * - otherwise clear the stored session and leave for
 *   `${REACT_APP_AGRONOD_KONTO_URL}/?redirectUrl=<current href>`.
 *
 * Fence returns the user to `redirectUrl` unchanged once registered. Clearing the
 * session first matters: the stored token still says `registered: false`, and
 * reusing it on return would send the user straight back to fence. With it gone,
 * `autoSignIn` fetches a fresh token through Keycloak's SSO session (no prompt)
 * and `onSignIn` restores the URL from the OIDC state.
 *
 * A per-tab breaker stops a Keycloak-mediated loop should the two apps disagree
 * about the flag.
 */
const LantbrukareRegistrationCheck: React.FC<RegistrationGuardProps> = ({ children }) => {
  const { pathname } = useLocation();
  const { userData, isLoading: oidcLoading, userManager } = useAuth();
  const { user, isLoading: userLoading, error: userError } = useUser();
  const fired = useRef(false);
  const [blocked, setBlocked] = useState(false);

  const applies =
    !isAuthCallbackRoute(pathname) &&
    !pathname.startsWith("/b2b") &&
    !oidcLoading &&
    !!userData &&
    realmFromIssuer(userData.profile?.iss) === "lantbrukare";
  const awaitingUser = applies && !user && userLoading && !userError;
  const mustRedirect = applies && !!user && isLantbrukareUser(user) && !isRegistered(user);

  useEffect(() => {
    // Never reset `fired`: StrictMode runs mount → cleanup → mount in dev, and a
    // second redirect would double-count in the breaker.
    if (!mustRedirect || fired.current) return;
    fired.current = true;

    if (!hasRegisteredClaim(user)) {
      console.warn(
        "[RegistrationGuard] /auth/me returned a lantbrukare without `registered`; is the agronod_profile scope on this client?",
      );
    }

    if (!recordRegistrationRedirect()) {
      setBlocked(true);
      return;
    }

    const target = buildRegistrationUrl(
      window.appConfig.REACT_APP_AGRONOD_KONTO_URL,
      window.location.href,
    );
    void userManager
      .removeUser()
      .catch((error: unknown) => {
        // Leaving anyway costs one extra hop on return, which the breaker tolerates.
        console.error(
          "[RegistrationGuard] Could not clear the stored session before leaving:",
          error,
        );
      })
      .then(() => navigateExternal(target));
  }, [mustRedirect, user, userManager]);

  if (blocked) {
    return <RedirectLoopNotice kontoUrl={window.appConfig.REACT_APP_AGRONOD_KONTO_URL} />;
  }
  // `fired` keeps the spinner up through the hand-off even if the session
  // state changes underneath us; children must not mount while leaving.
  if (fired.current || mustRedirect || awaitingUser) {
    return <FullScreenSpinner />;
  }
  return <>{children}</>;
};

/**
 * Registration gate for lantbrukare routes. Public routes have no AuthProvider
 * in their tree (see RealmRouter), so they bypass the check entirely rather
 * than call `useAuth()`.
 */
const RegistrationGuard: React.FC<RegistrationGuardProps> = ({ children }) => {
  const { pathname } = useLocation();

  if (isPublicRoute(pathname)) {
    return <>{children}</>;
  }

  return <LantbrukareRegistrationCheck>{children}</LantbrukareRegistrationCheck>;
};

export default RegistrationGuard;
